import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from '@/hooks/use-toast';
import { ArrowLeft, Mail, Lock, Trash2, User, Pen, Upload, X, Building2, Phone, MapPin, FileText, Save, Camera, Award, Gift, Settings, BookOpen, Download, BarChart3 } from 'lucide-react';
import { AppSettingsForm } from '@/components/settings/AppSettingsForm';
import { generateUserGuidePDF } from '@/utils/userGuidePdfExport';
import { USER_GUIDE_VERSION } from '@/data/userGuideContent';
import { DiscoveryStatistics } from '@/components/settings/DiscoveryStatistics';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface PractitionerInfo {
  id?: string;
  business_name: string;
  practitioner_name: string;
  address: string;
  phone: string;
  email: string;
  siret: string | null;
  signature_url: string | null;
  photo_url: string | null;
  photo_url_2: string | null;
  photo_url_3: string | null;
  certification_url: string | null;
  newsletter_gift_pdf_url: string | null;
}

export default function AccountSettings() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string>('');
  const [userId, setUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoUrl2, setPhotoUrl2] = useState<string | null>(null);
  const [photoUrl3, setPhotoUrl3] = useState<string | null>(null);
  const [certificationUrl, setCertificationUrl] = useState<string | null>(null);
  const [newsletterGiftPdfUrl, setNewsletterGiftPdfUrl] = useState<string | null>(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingPhoto2, setUploadingPhoto2] = useState(false);
  const [uploadingPhoto3, setUploadingPhoto3] = useState(false);
  const [uploadingCertification, setUploadingCertification] = useState(false);
  const [uploadingNewsletterPdf, setUploadingNewsletterPdf] = useState(false);
  const signatureInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef2 = useRef<HTMLInputElement>(null);
  const photoInputRef3 = useRef<HTMLInputElement>(null);
  const certificationInputRef = useRef<HTMLInputElement>(null);
  const newsletterPdfInputRef = useRef<HTMLInputElement>(null);

  // Practitioner info state
  const [practitionerInfo, setPractitionerInfo] = useState<PractitionerInfo>({
    business_name: '',
    practitioner_name: '',
    address: '',
    phone: '',
    email: '',
    siret: null,
    signature_url: null,
    photo_url: null,
    photo_url_2: null,
    photo_url_3: null,
    certification_url: null,
    newsletter_gift_pdf_url: null,
  });
  const [isSavingPractitioner, setIsSavingPractitioner] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
        setUserId(user.id);
        
        // Récupérer les infos praticien
        const { data: practitioner } = await supabase
          .from("practitioner_info")
          .select("*, newsletter_gift_pdf_url")
          .eq("user_id", user.id)
          .single();
        
        if (practitioner) {
          setPractitionerInfo({
            id: practitioner.id,
            business_name: practitioner.business_name || '',
            practitioner_name: practitioner.practitioner_name || '',
            address: practitioner.address || '',
            phone: practitioner.phone || '',
            email: practitioner.email || user.email,
            siret: practitioner.siret,
            signature_url: practitioner.signature_url,
            photo_url: practitioner.photo_url,
            photo_url_2: practitioner.photo_url_2,
            photo_url_3: practitioner.photo_url_3,
            certification_url: practitioner.certification_url,
            newsletter_gift_pdf_url: practitioner.newsletter_gift_pdf_url,
          });
          setSignatureUrl(practitioner.signature_url);
          setPhotoUrl(practitioner.photo_url);
          setPhotoUrl2(practitioner.photo_url_2);
          setPhotoUrl3(practitioner.photo_url_3);
          setCertificationUrl(practitioner.certification_url);
          setNewsletterGiftPdfUrl(practitioner.newsletter_gift_pdf_url);
        } else {
          setPractitionerInfo(prev => ({ ...prev, email: user.email || '' }));
        }
      }
    };
    getUser();
  }, []);

  const handleSavePractitionerInfo = async () => {
    if (!userId) return;

    setIsSavingPractitioner(true);
    try {
      const { data: existing } = await supabase
        .from("practitioner_info")
        .select("id")
        .eq("user_id", userId)
        .single();

      const dataToSave = {
        business_name: practitionerInfo.business_name,
        practitioner_name: practitionerInfo.practitioner_name,
        address: practitionerInfo.address,
        phone: practitionerInfo.phone,
        email: practitionerInfo.email,
        siret: practitionerInfo.siret || null,
      };

      if (existing) {
        const { error } = await supabase
          .from("practitioner_info")
          .update(dataToSave)
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("practitioner_info")
          .insert({
            user_id: userId,
            ...dataToSave,
          });
        if (error) throw error;
      }

      toast({
        title: "Informations enregistrées",
        description: "Vos informations praticien ont été mises à jour.",
      });
    } catch (error: any) {
      console.error("Erreur sauvegarde praticien:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer les informations.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPractitioner(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères.",
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast({
        title: "Mot de passe mis à jour",
        description: "Votre mot de passe a été changé avec succès.",
      });
      
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour le mot de passe.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      await supabase.auth.signOut();
      
      toast({
        title: "Compte supprimé",
        description: "Votre compte a été supprimé avec succès.",
      });
      
      navigate('/auth');
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le compte.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Déconnexion réussie",
        description: "À bientôt !",
      });
      navigate('/auth');
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de se déconnecter.",
        variant: "destructive",
      });
    }
  };

  const handleSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner une image.",
        variant: "destructive",
      });
      return;
    }

    setUploadingSignature(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `signatures/${userId}/signature_${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("assets")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("assets")
        .getPublicUrl(fileName);

      // Mettre à jour ou créer l'entrée practitioner_info
      const { data: existing } = await supabase
        .from("practitioner_info")
        .select("id")
        .eq("user_id", userId)
        .single();

      if (existing) {
        const { error } = await supabase
          .from("practitioner_info")
          .update({ signature_url: publicUrl })
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("practitioner_info")
          .insert({
            user_id: userId,
            signature_url: publicUrl,
            business_name: practitionerInfo.business_name || "",
            practitioner_name: practitionerInfo.practitioner_name || "",
            phone: practitionerInfo.phone || "",
            address: practitionerInfo.address || "",
            email: userEmail,
          });
        if (error) throw error;
      }

      setSignatureUrl(publicUrl);
      setPractitionerInfo(prev => ({ ...prev, signature_url: publicUrl }));
      toast({
        title: "Signature enregistrée",
        description: "Votre signature sera ajoutée aux PDF exportés.",
      });
    } catch (error: any) {
      console.error("Erreur upload signature:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer la signature.",
        variant: "destructive",
      });
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleRemoveSignature = async () => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from("practitioner_info")
        .update({ signature_url: null })
        .eq("user_id", userId);

      if (error) throw error;

      setSignatureUrl(null);
      setPractitionerInfo(prev => ({ ...prev, signature_url: null }));
      toast({
        title: "Signature supprimée",
        description: "Votre signature a été retirée.",
      });
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la signature.",
        variant: "destructive",
      });
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner une image.",
        variant: "destructive",
      });
      return;
    }

    setUploadingPhoto(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `photos/${userId}/photo_${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("assets")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("assets")
        .getPublicUrl(fileName);

      // Mettre à jour ou créer l'entrée practitioner_info
      const { data: existing } = await supabase
        .from("practitioner_info")
        .select("id")
        .eq("user_id", userId)
        .single();

      if (existing) {
        const { error } = await supabase
          .from("practitioner_info")
          .update({ photo_url: publicUrl })
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("practitioner_info")
          .insert({
            user_id: userId,
            photo_url: publicUrl,
            business_name: practitionerInfo.business_name || "",
            practitioner_name: practitionerInfo.practitioner_name || "",
            phone: practitionerInfo.phone || "",
            address: practitionerInfo.address || "",
            email: userEmail,
          });
        if (error) throw error;
      }

      setPhotoUrl(publicUrl);
      setPractitionerInfo(prev => ({ ...prev, photo_url: publicUrl }));
      toast({
        title: "Photo enregistrée",
        description: "Votre photo sera affichée sur le site vitrine.",
      });
    } catch (error: any) {
      console.error("Erreur upload photo:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer la photo.",
        variant: "destructive",
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from("practitioner_info")
        .update({ photo_url: null })
        .eq("user_id", userId);

      if (error) throw error;

      setPhotoUrl(null);
      setPractitionerInfo(prev => ({ ...prev, photo_url: null }));
      toast({
        title: "Photo supprimée",
        description: "Votre photo a été retirée.",
      });
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la photo.",
        variant: "destructive",
      });
    }
  };

  // Photo 2 handlers
  const handlePhotoUpload2 = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: "Erreur", description: "Veuillez sélectionner une image.", variant: "destructive" });
      return;
    }

    setUploadingPhoto2(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `photos/${userId}/photo2_${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage.from("assets").upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from("assets").getPublicUrl(fileName);

      const { data: existing } = await supabase.from("practitioner_info").select("id").eq("user_id", userId).single();

      if (existing) {
        const { error } = await supabase.from("practitioner_info").update({ photo_url_2: publicUrl }).eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("practitioner_info").insert({
          user_id: userId, photo_url_2: publicUrl, business_name: practitionerInfo.business_name || "",
          practitioner_name: practitionerInfo.practitioner_name || "", phone: practitionerInfo.phone || "",
          address: practitionerInfo.address || "", email: userEmail,
        });
        if (error) throw error;
      }

      setPhotoUrl2(publicUrl);
      setPractitionerInfo(prev => ({ ...prev, photo_url_2: publicUrl }));
      toast({ title: "Photo 2 enregistrée", description: "Votre photo sera affichée sur le site vitrine." });
    } catch (error: any) {
      console.error("Erreur upload photo 2:", error);
      toast({ title: "Erreur", description: "Impossible d'enregistrer la photo.", variant: "destructive" });
    } finally {
      setUploadingPhoto2(false);
    }
  };

  const handleRemovePhoto2 = async () => {
    if (!userId) return;
    try {
      const { error } = await supabase.from("practitioner_info").update({ photo_url_2: null }).eq("user_id", userId);
      if (error) throw error;
      setPhotoUrl2(null);
      setPractitionerInfo(prev => ({ ...prev, photo_url_2: null }));
      toast({ title: "Photo 2 supprimée" });
    } catch (error: any) {
      toast({ title: "Erreur", description: "Impossible de supprimer la photo.", variant: "destructive" });
    }
  };

  // Photo 3 handlers
  const handlePhotoUpload3 = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: "Erreur", description: "Veuillez sélectionner une image.", variant: "destructive" });
      return;
    }

    setUploadingPhoto3(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `photos/${userId}/photo3_${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage.from("assets").upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from("assets").getPublicUrl(fileName);

      const { data: existing } = await supabase.from("practitioner_info").select("id").eq("user_id", userId).single();

      if (existing) {
        const { error } = await supabase.from("practitioner_info").update({ photo_url_3: publicUrl }).eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("practitioner_info").insert({
          user_id: userId, photo_url_3: publicUrl, business_name: practitionerInfo.business_name || "",
          practitioner_name: practitionerInfo.practitioner_name || "", phone: practitionerInfo.phone || "",
          address: practitionerInfo.address || "", email: userEmail,
        });
        if (error) throw error;
      }

      setPhotoUrl3(publicUrl);
      setPractitionerInfo(prev => ({ ...prev, photo_url_3: publicUrl }));
      toast({ title: "Photo 3 enregistrée", description: "Votre photo sera affichée sur le site vitrine." });
    } catch (error: any) {
      console.error("Erreur upload photo 3:", error);
      toast({ title: "Erreur", description: "Impossible d'enregistrer la photo.", variant: "destructive" });
    } finally {
      setUploadingPhoto3(false);
    }
  };

  const handleRemovePhoto3 = async () => {
    if (!userId) return;
    try {
      const { error } = await supabase.from("practitioner_info").update({ photo_url_3: null }).eq("user_id", userId);
      if (error) throw error;
      setPhotoUrl3(null);
      setPractitionerInfo(prev => ({ ...prev, photo_url_3: null }));
      toast({ title: "Photo 3 supprimée" });
    } catch (error: any) {
      toast({ title: "Erreur", description: "Impossible de supprimer la photo.", variant: "destructive" });
    }
  };

  // Certification handlers
  const handleCertificationUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !isPdf) {
      toast({ title: "Erreur", description: "Veuillez sélectionner une image ou un PDF.", variant: "destructive" });
      return;
    }

    setUploadingCertification(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `certifications/${userId}/certification_${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage.from("assets").upload(fileName, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from("assets").getPublicUrl(fileName);

      const { data: existing } = await supabase.from("practitioner_info").select("id").eq("user_id", userId).single();

      if (existing) {
        const { error } = await supabase.from("practitioner_info").update({ certification_url: publicUrl }).eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("practitioner_info").insert({
          user_id: userId, certification_url: publicUrl, business_name: practitionerInfo.business_name || "",
          practitioner_name: practitionerInfo.practitioner_name || "", phone: practitionerInfo.phone || "",
          address: practitionerInfo.address || "", email: userEmail,
        });
        if (error) throw error;
      }

      setCertificationUrl(publicUrl);
      setPractitionerInfo(prev => ({ ...prev, certification_url: publicUrl }));
      toast({ title: "Diplôme enregistré", description: "Votre certification sera accessible depuis le site vitrine." });
    } catch (error: any) {
      console.error("Erreur upload certification:", error);
      toast({ title: "Erreur", description: "Impossible d'enregistrer le diplôme.", variant: "destructive" });
    } finally {
      setUploadingCertification(false);
    }
  };

  const handleRemoveCertification = async () => {
    if (!userId) return;
    try {
      const { error } = await supabase.from("practitioner_info").update({ certification_url: null }).eq("user_id", userId);
      if (error) throw error;
      setCertificationUrl(null);
      setPractitionerInfo(prev => ({ ...prev, certification_url: null }));
      toast({ title: "Diplôme supprimé" });
    } catch (error: any) {
      toast({ title: "Erreur", description: "Impossible de supprimer le diplôme.", variant: "destructive" });
    }
  };

  // Newsletter PDF handlers
  const handleNewsletterPdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    if (file.type !== 'application/pdf') {
      toast({ title: "Erreur", description: "Veuillez sélectionner un fichier PDF.", variant: "destructive" });
      return;
    }

    setUploadingNewsletterPdf(true);
    try {
      const fileName = `newsletter/${userId}/gift_${Date.now()}.pdf`;

      const { error: uploadError } = await supabase.storage.from("assets").upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from("assets").getPublicUrl(fileName);

      const { data: existing } = await supabase.from("practitioner_info").select("id").eq("user_id", userId).single();

      if (existing) {
        const { error } = await supabase.from("practitioner_info").update({ newsletter_gift_pdf_url: publicUrl }).eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("practitioner_info").insert({
          user_id: userId, newsletter_gift_pdf_url: publicUrl, business_name: practitionerInfo.business_name || "",
          practitioner_name: practitionerInfo.practitioner_name || "", phone: practitionerInfo.phone || "",
          address: practitionerInfo.address || "", email: userEmail,
        });
        if (error) throw error;
      }

      setNewsletterGiftPdfUrl(publicUrl);
      setPractitionerInfo(prev => ({ ...prev, newsletter_gift_pdf_url: publicUrl }));
      toast({ title: "PDF cadeau enregistré", description: "Ce PDF sera envoyé aux nouveaux inscrits à la newsletter." });
    } catch (error: any) {
      console.error("Erreur upload PDF newsletter:", error);
      toast({ title: "Erreur", description: "Impossible d'enregistrer le PDF.", variant: "destructive" });
    } finally {
      setUploadingNewsletterPdf(false);
    }
  };

  const handleRemoveNewsletterPdf = async () => {
    if (!userId) return;
    try {
      const { error } = await supabase.from("practitioner_info").update({ newsletter_gift_pdf_url: null }).eq("user_id", userId);
      if (error) throw error;
      setNewsletterGiftPdfUrl(null);
      setPractitionerInfo(prev => ({ ...prev, newsletter_gift_pdf_url: null }));
      toast({ title: "PDF cadeau supprimé" });
    } catch (error: any) {
      toast({ title: "Erreur", description: "Impossible de supprimer le PDF.", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </Button>

        <div className="space-y-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary">Paramètres du compte</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
              Gérez vos informations personnelles, la sécurité et les paramètres de l'application
            </p>
          </div>

          <Tabs defaultValue="account" className="space-y-6">
            {/* Mobile: horizontal scroll tabs */}
            <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
              <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-3 gap-1 min-w-full sm:min-w-0">
                <TabsTrigger value="account" className="gap-1 sm:gap-2 px-3 sm:px-4 text-xs sm:text-sm whitespace-nowrap">
                  <User className="w-3 h-3 sm:w-4 sm:h-4" />
                  Mon compte
                </TabsTrigger>
                <TabsTrigger value="app-settings" className="gap-1 sm:gap-2 px-3 sm:px-4 text-xs sm:text-sm whitespace-nowrap">
                  <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                  Paramètres
                </TabsTrigger>
                <TabsTrigger value="statistics" className="gap-1 sm:gap-2 px-3 sm:px-4 text-xs sm:text-sm whitespace-nowrap">
                  <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                  Statistiques
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Onglet Mon Compte */}
            <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Informations du compte
              </CardTitle>
              <CardDescription>
                Vos informations de connexion
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Adresse email</Label>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={userEmail}
                    disabled
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  L'email ne peut pas être modifié pour le moment
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Informations praticien pour les documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Informations praticien
              </CardTitle>
              <CardDescription>
                Ces informations apparaîtront sur vos factures et documents PDF
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Nom de l'activité</Label>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <Input
                      id="business_name"
                      value={practitionerInfo.business_name}
                      onChange={(e) => setPractitionerInfo(prev => ({ ...prev, business_name: e.target.value }))}
                      placeholder="Ex: REGARD DE NATURE"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="practitioner_name">Votre nom</Label>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <Input
                      id="practitioner_name"
                      value={practitionerInfo.practitioner_name}
                      onChange={(e) => setPractitionerInfo(prev => ({ ...prev, practitioner_name: e.target.value }))}
                      placeholder="Ex: Laura"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="practitioner_phone">Téléphone</Label>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <Input
                      id="practitioner_phone"
                      value={practitionerInfo.phone}
                      onChange={(e) => setPractitionerInfo(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Ex: 06 12 34 56 78"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="practitioner_email">Email professionnel</Label>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <Input
                      id="practitioner_email"
                      type="email"
                      value={practitionerInfo.email}
                      onChange={(e) => setPractitionerInfo(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Ex: contact@exemple.fr"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="siret">Numéro SIRET</Label>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <Input
                      id="siret"
                      value={practitionerInfo.siret || ''}
                      onChange={(e) => setPractitionerInfo(prev => ({ ...prev, siret: e.target.value }))}
                      placeholder="Ex: 123 456 789 00012"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Adresse postale</Label>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-2.5" />
                  <Textarea
                    id="address"
                    value={practitionerInfo.address}
                    onChange={(e) => setPractitionerInfo(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Ex: 123 rue de la Nature&#10;31000 Toulouse"
                    rows={2}
                    className="flex-1"
                  />
                </div>
              </div>

              <Button 
                onClick={handleSavePractitionerInfo}
                disabled={isSavingPractitioner}
                className="gap-2"
              >
                <Save className="w-4 h-4" />
                {isSavingPractitioner ? "Enregistrement..." : "Enregistrer les informations"}
              </Button>
            </CardContent>
          </Card>

          {/* Photo du praticien */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Photo du praticien
              </CardTitle>
              <CardDescription>
                Cette photo sera affichée dans la section "Une approche née de mon parcours" de votre site vitrine
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col items-center gap-4">
                {photoUrl ? (
                  <>
                    <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-primary/10 shadow-lg">
                      <img src={photoUrl} alt="Photo du praticien" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex justify-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} className="gap-2">
                        <Upload className="w-3 h-3" />
                        Changer
                      </Button>
                      <Button size="sm" variant="destructive" onClick={handleRemovePhoto} className="gap-2">
                        <X className="w-3 h-3" />
                        Supprimer
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center bg-muted/20 w-full max-w-xs">
                    <Camera className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <Button size="sm" variant="outline" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}>
                      {uploadingPhoto ? "Importation..." : "Importer une photo"}
                    </Button>
                  </div>
                )}
                <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              </div>
            </CardContent>
          </Card>

          {/* Diplôme / Certification */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Diplôme / Certification
              </CardTitle>
              <CardDescription>
                Ce diplôme sera accessible depuis le bouton "Ma Certification" sur votre site vitrine
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {certificationUrl ? (
                <div className="space-y-4">
                  <div className="border rounded-lg p-4 bg-muted/30">
                    {certificationUrl.toLowerCase().endsWith('.pdf') ? (
                      <div className="flex flex-col items-center gap-3">
                        <embed src={certificationUrl} type="application/pdf" className="w-full h-64 rounded" />
                        <a href={certificationUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline">
                          Ouvrir le PDF dans un nouvel onglet
                        </a>
                      </div>
                    ) : (
                      <img src={certificationUrl} alt="Votre diplôme" className="max-h-48 mx-auto object-contain" />
                    )}
                  </div>
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={() => certificationInputRef.current?.click()} disabled={uploadingCertification} className="gap-2">
                      <Upload className="w-4 h-4" />
                      Changer
                    </Button>
                    <Button variant="destructive" onClick={handleRemoveCertification} className="gap-2">
                      <X className="w-4 h-4" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed rounded-lg p-8 text-center bg-muted/20">
                  <Award className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-4">
                    Importez une image ou un PDF de votre diplôme ou certification
                  </p>
                  <Button variant="outline" onClick={() => certificationInputRef.current?.click()} disabled={uploadingCertification} className="gap-2">
                    <Upload className="w-4 h-4" />
                    {uploadingCertification ? "Envoi..." : "Importer un diplôme"}
                  </Button>
                </div>
              )}
              <input ref={certificationInputRef} type="file" accept="image/*,application/pdf" onChange={handleCertificationUpload} className="hidden" />
            </CardContent>
          </Card>

          {/* Signature */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pen className="w-5 h-5" />
                Signature
              </CardTitle>
              <CardDescription>
                Votre signature sera ajoutée aux PDF exportés (bilans iridologie, comptes-rendus...)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {signatureUrl ? (
                <div className="space-y-4">
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <img 
                      src={signatureUrl} 
                      alt="Votre signature" 
                      className="max-h-24 object-contain"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => signatureInputRef.current?.click()}
                      disabled={uploadingSignature}
                      className="gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Changer
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleRemoveSignature}
                      className="gap-2"
                    >
                      <X className="w-4 h-4" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border-2 border-dashed rounded-lg p-8 text-center bg-muted/20">
                    <Pen className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-4">
                      Importez une image de votre signature (PNG, JPG)
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => signatureInputRef.current?.click()}
                      disabled={uploadingSignature}
                      className="gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingSignature ? "Envoi..." : "Importer une signature"}
                    </Button>
                  </div>
                </div>
              )}
              <input
                ref={signatureInputRef}
                type="file"
                accept="image/*"
                onChange={handleSignatureUpload}
                className="hidden"
              />
            </CardContent>
          </Card>

          {/* Cadeau Newsletter */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Cadeau Newsletter
              </CardTitle>
              <CardDescription>
                PDF cadeau envoyé automatiquement aux nouveaux inscrits à la newsletter
              </CardDescription>
            </CardHeader>
            <CardContent>
              {newsletterGiftPdfUrl ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/20">
                    <FileText className="w-10 h-10 text-primary" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">PDF cadeau configuré</p>
                      <a 
                        href={newsletterGiftPdfUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        Voir le PDF
                      </a>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveNewsletterPdf}
                      className="gap-2"
                    >
                      <X className="w-4 h-4" />
                      Supprimer
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => newsletterPdfInputRef.current?.click()}
                    disabled={uploadingNewsletterPdf}
                    className="gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    {uploadingNewsletterPdf ? "Envoi..." : "Remplacer le PDF"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border-2 border-dashed rounded-lg p-8 text-center bg-muted/20">
                    <Gift className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-2">
                      Importez un PDF qui sera envoyé comme cadeau de bienvenue
                    </p>
                    <p className="text-xs text-muted-foreground mb-4 italic">
                      Ex: "Mieux comprendre son terrain – Les bases pour écouter son corps"
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => newsletterPdfInputRef.current?.click()}
                      disabled={uploadingNewsletterPdf}
                      className="gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingNewsletterPdf ? "Envoi..." : "Importer un PDF"}
                    </Button>
                  </div>
                </div>
              )}
              <input
                ref={newsletterPdfInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleNewsletterPdfUpload}
                className="hidden"
              />
            </CardContent>
          </Card>

          {/* Guide d'utilisation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Guide d'utilisation
              </CardTitle>
              <CardDescription>
                Téléchargez le guide complet d'utilisation de l'espace professionnel (v{USER_GUIDE_VERSION})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={async () => {
                  toast({ title: "Génération du PDF...", description: "Veuillez patienter" });
                  try {
                    await generateUserGuidePDF();
                    toast({ title: "Guide téléchargé", description: "Le PDF a été généré avec succès" });
                  } catch (error) {
                    toast({ title: "Erreur", description: "Impossible de générer le guide", variant: "destructive" });
                  }
                }}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Télécharger le guide d'utilisation (PDF)
              </Button>
            </CardContent>
          </Card>

          {/* Changer le mot de passe */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Sécurité
              </CardTitle>
              <CardDescription>
                Modifiez votre mot de passe pour sécuriser votre compte
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 caractères"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Retapez votre mot de passe"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isUpdatingPassword || !newPassword || !confirmPassword}
                >
                  {isUpdatingPassword ? "Mise à jour..." : "Mettre à jour le mot de passe"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Actions rapides */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
              <CardDescription>
                Gérer votre session et votre compte
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Se déconnecter</p>
                  <p className="text-sm text-muted-foreground">
                    Fermer votre session actuelle
                  </p>
                </div>
                <Button variant="outline" onClick={handleSignOut}>
                  Se déconnecter
                </Button>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-destructive">Zone de danger</p>
                  <p className="text-sm text-muted-foreground">
                    Supprimer définitivement votre compte
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="gap-2">
                      <Trash2 className="w-4 h-4" />
                      Supprimer le compte
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cette action est irréversible. Cela supprimera définitivement votre
                        compte et toutes vos données de nos serveurs.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        disabled={isDeletingAccount}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {isDeletingAccount ? "Suppression..." : "Oui, supprimer mon compte"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
            </TabsContent>

            {/* Onglet Paramètres de l'application */}
            <TabsContent value="app-settings">
              <AppSettingsForm />
            </TabsContent>

            {/* Onglet Statistiques */}
            <TabsContent value="statistics">
              <DiscoveryStatistics />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}